import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import type { Role } from "@/api/types";

function typing(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable || !!el.closest("[role=dialog] input");
}

function moveRow(delta: 1 | -1) {
  const rows = Array.from(document.querySelectorAll<HTMLElement>("[data-row-id], [data-timeline-row]"));
  if (!rows.length) return;
  const i = rows.indexOf(document.activeElement as HTMLElement);
  const next = rows[Math.min(rows.length - 1, Math.max(0, i + delta))] ?? rows[0]!;
  next.focus();
  next.scrollIntoView({ block: "nearest" });
}

export function useGlobalShortcuts(opts: { role?: Role; openPalette: () => void; openShortcuts: () => void }) {
  const navigate = useNavigate();
  const pendingG = useRef<number | null>(null);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const o = optsRef.current;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        o.openPalette();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey || typing(e.target)) return;
      if (pendingG.current !== null) {
        window.clearTimeout(pendingG.current);
        pendingG.current = null;
        const map: Record<string, string | null> = {
          d: "/",
          r: "/runs",
          c: "/catalog",
          a: o.role === "ADMIN" ? "/admin" : null,
          u: o.role === "ADMIN" ? "/admin/users" : null,
        };
        const to = map[e.key];
        if (to) {
          e.preventDefault();
          navigate(to);
        }
        return;
      }
      switch (e.key) {
        case "g":
          pendingG.current = window.setTimeout(() => (pendingG.current = null), 1_000);
          break;
        case "?":
          e.preventDefault();
          o.openShortcuts();
          break;
        case "/":
          e.preventDefault();
          document.querySelector<HTMLElement>("[data-page-search] input, [role=searchbox]")?.focus();
          break;
        case "n":
          if (o.role === "TESTER" || o.role === "ADMIN") navigate("/runs/new");
          break;
        case "j":
          moveRow(1);
          break;
        case "k":
          moveRow(-1);
          break;
        case "f":
          window.dispatchEvent(new CustomEvent("ats:toggle-follow"));
          break;
        default:
          break;
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);
}
